import React, { useState } from 'react';
import { PlatformAgreementAdmin } from '../panels/PlatformAgreementAdmin';
import { getTheme, useThemeMode } from '../theme';
import { HunterApprovalAdmin } from '../panels/HunterApprovalPanels';
import { AdminListingsSection, BackupSection, ImportedMarksSection } from '../panels';
import { ModerationQueue } from '../panels/ModerationQueue';
import { SupportTickets } from '../panels/SupportTickets';
import { QAdminPanel } from '../panels/QAdminPanel';
import { RetentionPanel } from '../panels/RetentionPanel';
import { BookkeepingPanel } from '../panels/BookkeepingPanel';
import { IntegrationVisibilityAdmin } from '../panels/IntegrationVisibilityAdmin';

/**
 * Admin-flikarna. Ny flik = en rad här: `key` (unik), `label` (visas i flikraden)
 * och `render` (panelen). Ordningen här är ordningen i flikraden.
 */
export interface AdminTab { key: string; label: string; render: () => React.ReactNode }
export const ADMIN_TABS: AdminTab[] = [
  { key: 'moderering', label: 'Moderering', render: () => <ModerationQueue /> },
  { key: 'support', label: 'Support', render: () => <SupportTickets /> },
  { key: 'jagare', label: 'Godkänn jägare', render: () => <HunterApprovalAdmin /> },
  { key: 'annonser', label: 'Annonser', render: () => <AdminListingsSection /> },
  { key: 'importerade', label: 'Importerade marker', render: () => <ImportedMarksSection /> },
  { key: 'avtal', label: 'Markägaravtal', render: () => <PlatformAgreementAdmin /> },
  { key: 'gallring', label: 'Gallring', render: () => <RetentionPanel /> },
  { key: 'bokforing', label: 'Bokföring', render: () => <BookkeepingPanel /> },
  { key: 'integrationer', label: 'Integrationer', render: () => <IntegrationVisibilityAdmin /> },
  { key: 'backup', label: 'Backup', render: () => <BackupSection /> },
  { key: 'q', label: 'Q', render: () => <QAdminPanel /> },
];

const TAB_KEY = 'qvenari_admin_tab';
function savedTab(): string {
  try { const k = sessionStorage.getItem(TAB_KEY); if (k && ADMIN_TABS.some(t => t.key === k)) return k; } catch { /* privat läge */ }
  return ADMIN_TABS[0].key;
}

/** Egen sida för admin (meny → Admin). Andra ser bara ett nej; API:t kräver admin för varje anrop. */
export function VAdmin({ isAdmin }: { isAdmin: boolean }) {
  const T = getTheme(useThemeMode().mode);
  const [tab, setTab] = useState(savedTab);
  const pick = (k: string) => { setTab(k); try { sessionStorage.setItem(TAB_KEY, k); } catch { /* privat läge */ } };
  const page: React.CSSProperties = { paddingTop: 160, paddingBottom: 32, paddingLeft: 'clamp(12px,3vw,28px)', paddingRight: 'clamp(12px,3vw,28px)', maxWidth: 860, margin: '0 auto' };

  if (!isAdmin) {
    return (
      <div className="view-pad" style={{ ...page, textAlign: 'center' }}>
        <div style={{ color: T.text, fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Ingen behörighet</div>
        <div style={{ color: T.textSub, fontSize: 14 }}>Den här sidan är bara för administratörer.</div>
      </div>
    );
  }

  const active = ADMIN_TABS.find(t => t.key === tab) ?? ADMIN_TABS[0];
  return (
    <div className="view-pad" style={page}>
      <h1 style={{ color: T.text, fontSize: 20, fontWeight: 700, margin: '0 0 16px' }}>Admin</h1>
      <div role="tablist" className="hide-scrollbar" style={{ display: 'flex', gap: 22, overflowX: 'auto', scrollbarWidth: 'none', borderBottom: `1px solid ${T.border}`, marginBottom: 20 }}>
        {ADMIN_TABS.map(t => {
          const on = t.key === active.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => pick(t.key)}
              style={{ background: 'none', border: 'none', borderBottom: `2px solid ${on ? T.amber : 'transparent'}`, padding: '0 0 10px', marginBottom: -1, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: on ? T.text : T.textSub, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel">{active.render()}</div>
    </div>
  );
}
